import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CandleChart } from "@/components/CandleChart";
import { CheckCircle2, XCircle, RefreshCw, Sparkles } from "lucide-react";

const OPTIONS = [
  { key: "up", label: "Uptrend", desc: "Higher highs & higher lows" },
  { key: "down", label: "Downtrend", desc: "Lower highs & lower lows" },
  { key: "side", label: "Sideways", desc: "Ranging, no clear direction" },
];

function genCandles(trend) {
  const n = 58;
  let price = 100 + Math.random() * 60;
  const candles = [];
  const baseDrift = { up: 0.0034, down: -0.0034, side: 0 }[trend];
  const phase = Math.random() * Math.PI * 2;
  const now = Math.floor(Date.now() / 1000);
  for (let i = 0; i < n; i++) {
    // stronger noise makes the direction less obvious
    const noise = (Math.random() - 0.5) * 0.038;
    let drift;
    if (trend === "side") {
      // ranging with a subtle random bias so it can be mistaken for a weak trend
      drift = Math.sin(i / 4 + phase) * 0.013 + (Math.random() - 0.5) * 0.004;
    } else {
      // occasional counter-trend pullbacks / retracements
      const pullback = Math.random() < 0.22;
      drift = pullback ? -baseDrift * (0.5 + Math.random() * 0.9) : baseDrift * (0.7 + Math.random());
    }
    const o = price;
    price = Math.max(1, price * (1 + drift + noise));
    const c = price;
    const spread = (Math.random() * 0.5 + 0.35) * Math.abs(c - o) + o * 0.005;
    candles.push({ t: now - (n - i) * 3600, o: +o.toFixed(2), c: +c.toFixed(2), h: +(Math.max(o, c) + spread).toFixed(2), l: +(Math.min(o, c) - spread).toFixed(2), v: Math.random() * 1000 + 200 });
  }
  return candles;
}

export default function Exercises() {
  const { setProgress } = useAuth();
  const [trend, setTrend] = useState(() => OPTIONS[Math.floor(Math.random() * 3)].key);
  const [candles, setCandles] = useState(() => genCandles(trend));
  const [answer, setAnswer] = useState(null);
  const [streak, setStreak] = useState(0);

  const next = useCallback(() => {
    const t = OPTIONS[Math.floor(Math.random() * 3)].key;
    setTrend(t);
    setCandles(genCandles(t));
    setAnswer(null);
  }, []);

  const choose = async (key) => {
    if (answer) return;
    const correct = key === trend;
    setAnswer(key);
    setStreak((s) => (correct ? s + 1 : 0));
    if (correct) toast.success("Correct! +10 XP"); else toast.error("Not quite — see the explanation");
    try {
      const { data } = await api.post("/progress/exercise", { correct });
      setProgress(data);
    } catch {}
  };

  const correctOpt = OPTIONS.find((o) => o.key === trend);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Trend Exercises</h1>
          <p className="text-slate-400 mt-1">Look at the chart and identify the trend. Earn XP for each correct answer.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs font-mono text-slate-500 uppercase tracking-wider">Streak</div>
          <div className="font-mono text-2xl font-bold text-amber-400">{streak}🔥</div>
        </div>
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-4">
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-3 px-2">
          <Sparkles className="w-4 h-4 text-blue-400" /> What is the trend of this chart?
        </div>
        <CandleChart candles={candles} height={360} showVolume={false} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {OPTIONS.map((o) => {
          const isAnswer = answer === o.key;
          const isCorrect = trend === o.key;
          let cls = "border-slate-700 bg-slate-900/60 hover:border-blue-500/50";
          if (answer) {
            if (isCorrect) cls = "border-emerald-500/60 bg-emerald-500/10";
            else if (isAnswer) cls = "border-red-500/60 bg-red-500/10";
            else cls = "border-slate-800 bg-slate-900/40 opacity-60";
          }
          return (
            <button key={o.key} data-testid={`trend-option-${o.key}`} onClick={() => choose(o.key)} disabled={!!answer}
              className={`text-left rounded-xl border p-4 transition-all ${cls}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-100">{o.label}</span>
                {answer && isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                {answer && isAnswer && !isCorrect && <XCircle className="w-5 h-5 text-red-400" />}
              </div>
              <p className="text-xs text-slate-400 mt-1">{o.desc}</p>
            </button>
          );
        })}
      </div>

      {answer && (
        <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl p-5 animate-in" data-testid="exercise-explanation">
          <p className="text-slate-200">
            The correct answer is <span className="font-semibold text-blue-300">{correctOpt.label}</span>. {correctOpt.key === "up" && "Notice how each swing high and swing low is higher than the last — that structure defines an uptrend."}
            {correctOpt.key === "down" && "Each swing high and swing low is lower than the last — that structure defines a downtrend."}
            {correctOpt.key === "side" && "Price oscillates within a horizontal range with no clear directional bias — a sideways/ranging market."}
          </p>
          <button data-testid="next-exercise-btn" onClick={next} className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors">
            <RefreshCw className="w-4 h-4" /> Next chart
          </button>
        </div>
      )}
    </div>
  );
}
